import { Formula, FormulaRule } from '@otw/models'

export interface CalculatePriceParams {
  input: {
    year: number
    mileage: number
  }
  formula: Formula
  rules: FormulaRule[]
}

export interface CalculatePriceResult {
  price: number
  yearAdjustment: number
  mileageAdjustment: number
}

export const calculatePrice = ({
  input,
  formula,
  rules,
}: CalculatePriceParams): CalculatePriceResult => {
  const { year, mileage } = input
  const basePrice = formula.maxPrice
  const minPrice = formula.minPrice || 0

  const yearRules = rules.filter(r => r.targetFactor === 'year' && r.status === 'active')
  const mileageRules = rules.filter(r => r.targetFactor === 'mileage' && r.status === 'active')

  let price = basePrice
  let yearDepreciation = 0
  let mileageDepreciation = 0

  for (const rule of yearRules) {
    if (year <= (rule.lowerBound ?? new Date().getFullYear())) {
      const yearsInRange =
        rule.lowerBound - Math.min(rule.upperBound ?? new Date().getFullYear(), year)

      if (yearsInRange > 0) {
        const depreciation =
          rule.operationType === 'subtract'
            ? yearsInRange * rule.operationValue
            : yearsInRange * ((basePrice * rule.operationValue) / 100)

        yearDepreciation += depreciation
        price -= depreciation
      }
    }
  }

  for (const rule of mileageRules) {
    if (mileage >= (rule.lowerBound ?? 0)) {
      const milesInRange = rule.upperBound
        ? Math.min(mileage, rule.upperBound) - (rule.lowerBound ?? 0)
        : mileage - (rule.lowerBound ?? 0)

      if (milesInRange > 0) {
        const depreciation =
          rule.operationType === 'subtract'
            ? milesInRange * rule.operationValue
            : milesInRange * ((basePrice * rule.operationValue) / 100)

        mileageDepreciation += depreciation
        price -= depreciation
      }
    }
  }

  const finalPrice = Math.max(minPrice ?? 0, price)

  return {
    price: Math.round(finalPrice),
    yearAdjustment: yearDepreciation,
    mileageAdjustment: mileageDepreciation,
  }
}
